import { keepPreviousData, useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { queryKeys } from '~/api/queryKeys';
import { jitteredStaleTime } from '~/utils/jitter';

import { queryCurationRunProducts } from '../services/curation.service';
import {
  getRunLineageGridPage,
  getRunLineageIssueGroups,
  getRunLineageIssueListings,
  getRunLineageIssues,
  getRunLineageListings,
  getRunLineageStageJoin,
  getRunLineageSummary,
} from '../services/lineage-images.service';
import type { FeedProductRow } from '../types/curation.types';
import type {
  LineageListing,
  RunLineageListingsQuery,
  RunLineageQuery,
  Stage,
} from '../types/lineage-images.api.types';
import type { LineageJoinTarget } from '../types/lineage-images.types';
import { readWithFallback } from '../utils/catalog-capabilities';
import {
  countIssueListings,
  fromIssueGroupsRead,
  groupLineageIssues,
  ISSUE_MORE_PAGE_SIZE,
  ISSUE_SAMPLE_PER_GROUP,
  type LineageIssueGroup,
  type LineageIssuePlan,
  type LineageIssuesView,
} from '../utils/lineage-issues';

/**
 * One page of the lineage grid: §49 `…/lineage/listings` (one row per listing, every stage's
 * newest attempt), or on an older backend the paged stage's flat `…/lineage` page (`source:
 * 'fallback'`), which the caller completes with `useRunLineageStageJoins`. Keeps the previous
 * page visible while paging; `enabled` is false while the issues view replaces the grid.
 */
export function useRunLineageGrid(
  runId: string,
  listingsQuery: RunLineageListingsQuery,
  fallbackQuery: RunLineageQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.catalogService.runLineageListings(runId, {
      listings: listingsQuery,
      fallback: fallbackQuery,
    }),
    queryFn: () => getRunLineageGridPage(runId, listingsQuery, fallbackQuery),
    enabled: enabled && !!runId,
    placeholderData: keepPreviousData,
  });
}

/** Rows a one-product listing read asks for (one listing is normally one row). */
const ONE_LISTING_PAGE = 20;

/**
 * Design B — one product's listings for the Listings view (a Products row's "N listings"
 * badge): one §49 `…/lineage/listings?feedProductId=` read per listing id, joined. The endpoint
 * narrows to a single listing, so a product's few listings are a few small reads, cached per id.
 */
export function useRunLineageListingsFor(runId: string, feedProductIds: readonly string[]) {
  return useQueries({
    queries: feedProductIds.map((feedProductId) => ({
      queryKey: queryKeys.catalogService.runLineageListings(runId, {
        listings: { feedProductId, size: ONE_LISTING_PAGE },
      }),
      queryFn: () => getRunLineageListings(runId, { feedProductId, size: ONE_LISTING_PAGE }),
      enabled: !!runId,
    })),
    // `combine` keeps the joined result referentially stable between renders.
    combine: (results) => ({
      items: results.flatMap((r): LineageListing[] => r.data?.items ?? []),
      isPending: results.some((r) => r.isPending),
      // Any failed per-listing read is an error (with Retry), never a silently shorter list.
      isError: results.some((r) => r.isError),
      error: results.find((r) => r.error)?.error ?? null,
      isFetching: results.some((r) => r.isFetching),
      refetch: () => {
        for (const r of results) void r.refetch();
      },
    }),
  });
}

/** A run's lineage counts per stage + IMPORT dispositions. */
export function useRunLineageSummary(runId: string) {
  return useQuery({
    queryKey: queryKeys.catalogService.runLineageSummary(runId),
    queryFn: () => getRunLineageSummary(runId),
    enabled: !!runId,
  });
}

/** One stage the grid needs filled in, for the listings whose page row lacks it. */
export interface LineageStageJoinRequest {
  stage: Stage;
  targets: LineageJoinTarget[];
}

/**
 * Fallback for backends without §49 `…/lineage/listings`.
 * The other stages' outcomes for the grid's current page: per stage, the aligned page (same
 * index and size) plus at most a few neighbours, joined by listing. Results line up with
 * `joins`; a stage with no target listings is not read.
 */
export function useRunLineageStageJoins(
  runId: string,
  page: number,
  size: number,
  joins: LineageStageJoinRequest[],
) {
  return useQueries({
    queries: joins.map(({ stage, targets }) => ({
      queryKey: queryKeys.catalogService.runLineage(runId, {
        join: stage,
        page,
        size,
        listings: targets.map((target) => target.key),
      }),
      queryFn: () => getRunLineageStageJoin(runId, { stage, page, size, targets }),
      enabled: !!runId && targets.length > 0,
    })),
  });
}

/** How long a run's grouped issues stay fresh (the server caches §49 groups ~15 s; the walk is costly). */
const ISSUES_STALE_MS = 5 * 60_000;

/**
 * Issues by reason: §49 `…/lineage/issues` (grouped server-side over the whole run, top 50
 * groups), or on an older backend the capped walk of failed / still-flagged / skipped rows per
 * `plan`, grouped here. Read only while the Issues view is open; under the run's key.
 */
export function useRunLineageIssues(runId: string, plan: LineageIssuePlan, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.catalogService.runLineageIssues(runId, {
      walks: plan.walks,
      capped: plan.capped,
    }),
    queryFn: async (): Promise<LineageIssuesView> => {
      const result = await readWithFallback({
        capability: 'lineageIssues',
        read: () => getRunLineageIssueGroups(runId, ISSUE_SAMPLE_PER_GROUP),
        fallback: () => getRunLineageIssues(runId, plan.walks),
      });
      if (result.source === 'new') return fromIssueGroupsRead(result.data);
      const groups = groupLineageIssues(result.data.items);
      const capped = plan.capped || result.data.truncated;
      return {
        groups,
        listings: countIssueListings(groups),
        partial: capped ? { kind: 'rows', scanned: result.data.items.length } : null,
      };
    },
    enabled: enabled && !!runId,
    staleTime: jitteredStaleTime(ISSUES_STALE_MS),
  });
}

/**
 * The rest of one §49 issue group past its inline sample, `ISSUE_MORE_PAGE_SIZE` a page from
 * the group's `nextCursor`. Idle until `enabled` (the reviewer asked for more).
 */
export function useRunLineageIssueMore(runId: string, group: LineageIssueGroup, enabled: boolean) {
  const more = group.more;
  return useInfiniteQuery({
    queryKey: queryKeys.catalogService.runLineageIssueListings(runId, {
      stage: group.stage,
      reason: more?.reason ?? '',
      from: more?.nextCursor ?? '',
    }),
    queryFn: ({ pageParam }) =>
      getRunLineageIssueListings(runId, {
        stage: group.stage,
        reason: more?.reason ?? '',
        after: pageParam,
        size: ISSUE_MORE_PAGE_SIZE,
      }),
    initialPageParam: more?.nextCursor ?? null,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: enabled && !!runId && !!more,
    staleTime: jitteredStaleTime(ISSUES_STALE_MS),
  });
}

/** The run-products lookup takes 1000 ids but returns at most 100 rows a page — one POST per 100. */
const LOOKUP_CHUNK = 100;

/**
 * Title / thumb / market of a set of the run's listings through the curation run-products lookup,
 * one POST per 100 ids (shares the lookup's cache key). Disabled until a group is expanded.
 */
export function useRunListingsLookup(
  runId: string,
  feedProductIds: readonly string[],
  enabled: boolean,
): { listings: Map<string, FeedProductRow>; isPending: boolean } {
  const chunks = useMemo(() => {
    const unique = [...new Set(feedProductIds)];
    const out: string[][] = [];
    for (let i = 0; i < unique.length; i += LOOKUP_CHUNK)
      out.push(unique.slice(i, i + LOOKUP_CHUNK));
    return out;
  }, [feedProductIds]);
  const results = useQueries({
    queries: chunks.map((productIds) => {
      // List-row keys only (title / thumb / market): `summary` skips descriptions and raw payloads.
      const params = { productIds, page: 1, size: productIds.length, fields: 'summary' as const };
      return {
        queryKey: queryKeys.catalogService.curationProducts(runId, { ...params }),
        queryFn: () => queryCurationRunProducts(runId, params),
        enabled: enabled && !!runId,
        staleTime: jitteredStaleTime(60_000),
      };
    }),
  });
  const listings = new Map<string, FeedProductRow>();
  for (const result of results) {
    for (const row of result.data?.page.items ?? []) listings.set(row.feedProductId, row);
  }
  return { listings, isPending: enabled && results.some((r) => r.isPending) };
}
